import React from 'react'
import BackButton from '../../../../components/BackButton'
import LanguageStrings from '../../../../i18n/locales'
import { useNavigate } from 'react-router-dom'
import { Button } from '../../../../components/Button'
import { Card, CircularProgress } from '@mui/material'
import { type Option } from '../../../../components/GenXDropdown'
import Modal from '../../../../components/Modal'
import type { CreateMappingPostRequest } from '../../types'
import { createNewDeviceKeyMapping } from '../../redux/actionCreator'
import { type RootState } from '../../../../store'
import { type ConnectedProps, connect } from 'react-redux'
import AuthError from '../../../../components/ErrorComponents/AuthError'
import type { CreateKeyMappingState } from '../../redux/createKeyMappingSlice'
import KeyTypeMappingInformation from './KeyTypeMappingInformation'

const KeyTypeMappings = LanguageStrings().HSMConfiguration.KeyTypeMappings

const AddKeyTypeMappingComponent = (props: PropsFromRedux): JSX.Element => {
  const [labelOfHybridKek, setLabelOfHybridKek] = React.useState<Option | null>(null)
  const [tenant, setTenant] = React.useState<Option | null>(null)
  const [keyType, setKeyType] = React.useState<Option | null>(null)
  const [error, setError] = React.useState<boolean>(false)
  const [showConfirmation, setShowConfirmation] = React.useState(false)
  const [showCreatePopup, setShowCreatePopup] = React.useState(false)
  const [nameErrorInit, setNameErrorInit] = React.useState(false)
  const [tenantOptions, setTenantOptions] = React.useState<Option[]>([])
  const [labelOfHybridKekOptions, setLabelOfHybridKekOptions] = React.useState<Option[]>([])
  const [keyTypeOptions, setKeyTypeOptions] = React.useState<Option[]>([])
  const navigate = useNavigate()

  React.useEffect(() => {
    if (!showCreatePopup && !showConfirmation && props.createNewDeviceKeyMappingState.isLoading) {
      setShowCreatePopup(true)
    }
  }, [props.createNewDeviceKeyMappingState.isLoading])

  const resetKeyTypeMappingState = (): void => {
    setLabelOfHybridKek(null)
    setTenant(null)
    setKeyType(null)
    setError(false)
    setNameErrorInit(false)
  }
  const handeleCancelClick = (): void => {
    resetKeyTypeMappingState()
    navigate(-1)
  }

  const handleCreateKeyMapping = (): void => {
    props.createNewDeviceKeyMapping({
      hybridKek: labelOfHybridKek !== null ? labelOfHybridKek.value : '',
      keyName: keyType !== null ? keyType.value : '',
      tenant: tenant !== null ? tenant.value : ''
    })
  }

  const handleCreateKeyMappingWithFailure = (): JSX.Element => {
    const km = props.createNewDeviceKeyMappingState
    const hasErrorString = typeof km.error === 'string' && km.error.length > 0
    const noError = km.error == null

    if (km.httpStatus === 200 && noError) {
      return (
        <Modal
          buttonTypeNegative='hollow'
          title={KeyTypeMappings.CreatingKeyTypeMapping}
          LabelPositive={KeyTypeMappings.OK}
          onClickPositive={() => { navigate('/device-key-mappings') }}
          onClose={() => { navigate('/device-key-mappings') }}
        >
          <p style={{ paddingTop: '1.2em' }}>{KeyTypeMappings.KeyTypeMappingHasBeenCreatedSuccessfully}</p>
        </Modal>
      )
    }

    if (km.httpStatus === 401) {
      return (
        <Modal titleStyle={{ paddingRight: '2em' }} width={'fit-content'} height={'fit-content'} buttonTypeNegative='hollow' LabelNegative={KeyTypeMappings.Close}
               onClickNegative={() => { setShowCreatePopup(false) }}
               onClose={() => { setShowCreatePopup(false) }} title={KeyTypeMappings.CreatingKeyTypeMapping}>
          <div className='authError'>
            <AuthError className={'errorCompInsideModal'} errorMessage="Unauthorized"/>
          </div>
        </Modal>
      )
    }

    if (km.httpStatus === 403) {
      return (
        <Modal titleStyle={{ paddingRight: '2em' }} width={'fit-content'} height={'fit-content'} buttonTypeNegative='hollow' LabelNegative={KeyTypeMappings.Close}
               onClickNegative={() => { setShowCreatePopup(false) }}
               onClose={() => { setShowCreatePopup(false) }} title={KeyTypeMappings.CreatingKeyTypeMapping}>
          <div className='authError'>
            <AuthError className={'errorCompInsideModal'} errorMessage="accessForbidden"/>
          </div>
        </Modal>
      )
    }

    if ((km.httpStatus ?? 0) >= 400 || hasErrorString) {
      return (
        <Modal width={'fit-content'} height={'fit-content'}
               LabelPositive={KeyTypeMappings.OK}
               onClickPositive={() => { setShowCreatePopup(false) }}
               onClose={() => { setShowCreatePopup(false) }} title={KeyTypeMappings.CreatingKeyTypeMapping}>
          <div className='authError'>
            <AuthError className={'errorCompInsideModal'} customError={typeof km.error === 'string' ? km.error : 'Key type mapping could not be created'} />
          </div>
        </Modal>
      )
    }

    return (
      <Modal title={KeyTypeMappings.CreatingKeyTypeMapping}>
        <CircularProgress size={20} />
      </Modal>
    )
  }

  return (
        <div style={{ width: 'calc(100vw - 3.5rem)', paddingRight: '2rem' }}>
            <div className='link-breadcrumb'>
                <span data-testid='assetManagementbreadcrumb' onClick={ () => { navigate('/card') }} className='assetmgmtli'>{KeyTypeMappings.HSMKeyManagement}</span>
                <span style={{ padding: '0 0.5em' }}>/</span>
                <span onClick={() => { navigate('/device-key-mappings') }} className='assetmgmtli'>{KeyTypeMappings.KeyTypeMapping}</span>
                <span style={{ padding: '0 0.5em' }}>/</span>
                <span>{KeyTypeMappings.AddKeyTypeMapping}</span>
            </div>
            <div className='topUtilityBar'>
                <BackButton/>
                <span id='componentTitle' data-testid='create-key-type'>{KeyTypeMappings.CreatingKeyTypeMapping}</span>
            </div>
            <br />
            {
                        <Card sx={{ borderRadius: '10px', backgroundColor: '#272727', color: '#F0F0F0 !important', padding: '25px', overflow: 'visible' }}>
                            <div style={{ height: 2, borderTop: '2px solid #707070', width: '100%', margin: '20px 0' }}></div>
                            <KeyTypeMappingInformation
                                error={error}
                                setError={setError}
                                tenant={tenant}
                                setTenant={setTenant}
                                keyType={keyType}
                                setKeyType={setKeyType}
                                labelOfHybridKek={labelOfHybridKek}
                                setLabelOfHybridKek={setLabelOfHybridKek}
                                setNameErrorInit={setNameErrorInit}
                                nameErrorInit={nameErrorInit} tenantOptions={tenantOptions} setTenantOptions={setTenantOptions} labelOfHybridKekOptions={labelOfHybridKekOptions} setLabelOfHybridKekOptions={setLabelOfHybridKekOptions} keyTypeOptions={keyTypeOptions} setKeyTypeOptions={setKeyTypeOptions}/>
                        </Card>
            }
            <div style={{ width: '100%', height: '80px', backgroundColor: '#272727', margin: '50px 0 10px 0', borderRadius: 10 }}>
                <div style={{ height: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 1em' }}>
                    <div>
                        <Button type='hollow' onClick={() => { handeleCancelClick() }}>{KeyTypeMappings.Cancel}</Button>
                    </div>
                        {
                          <Button
                            dataTestId='next-btn-select'
                            disabled={labelOfHybridKek === null || keyType === null || tenant === null || error || (keyType?.value?.trim() ?? '') === '' || (tenant?.value?.trim() ?? '') === '' || (labelOfHybridKek?.value?.trim() ?? '') === ''}
                            primaryColor={(labelOfHybridKek === null || keyType === null || tenant === null || error || (keyType?.value?.trim() ?? '') === '' || (tenant?.value?.trim() ?? '') === '' || (labelOfHybridKek?.value?.trim() ?? '') === '') ? '#B0B0B0' : undefined}
                            sx={{ cursor: (labelOfHybridKek === null || keyType === null || tenant === null || error || (keyType?.value?.trim() ?? '') === '' || (tenant?.value?.trim() ?? '') === '' || (labelOfHybridKek?.value?.trim() ?? '') === '') ? 'not-allowed' : 'pointer' }}
                            onClick={() => {
                              if (labelOfHybridKek === null || keyType === null || tenant === null || error || (keyType?.value?.trim() ?? '') === '' || (tenant?.value?.trim() ?? '') === '' || (labelOfHybridKek?.value?.trim() ?? '') === '') {
                                return
                              }
                              setShowConfirmation(true)
                            }}
                          >
                            {KeyTypeMappings.Create}
                          </Button>
                        }
                </div>
            </div>
            {showConfirmation && (
              <Modal
                buttonTypeNegative='hollow'
                title={KeyTypeMappings.Confirm}
                LabelNegative={KeyTypeMappings.Cancel}
                LabelPositive={KeyTypeMappings.Confirm}
                onClickPositive={() => {
                  setShowConfirmation(false)
                  setShowCreatePopup(true)
                  handleCreateKeyMapping()
                }}
                onClose={() => { setShowConfirmation(false) }}
                onClickNegative={() => { setShowConfirmation(false) }}
              >
                <p style={{ paddingTop: '1.2em' }}>{KeyTypeMappings.DoYouWantToCreateThisDeviceKeyMapping}</p>
              </Modal>
            )}
            {
                showCreatePopup &&
                handleCreateKeyMappingWithFailure()
            }
        </div>
  )
}

interface DispatchToProps {
  createNewDeviceKeyMapping: (createMappingPostRequest: CreateMappingPostRequest) => void
}

const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  createNewDeviceKeyMapping: (createMappingPostRequest: CreateMappingPostRequest) => dispatch(createNewDeviceKeyMapping(createMappingPostRequest))
})

interface StateToProps {
  createNewDeviceKeyMappingState: CreateKeyMappingState
}

const mapStateToProps = (state: RootState): StateToProps => ({
  createNewDeviceKeyMappingState: state.createKeyMapping
})

const connector = connect(mapStateToProps, mapDispatchToProps)

type PropsFromRedux = ConnectedProps<typeof connector>

export default connector(AddKeyTypeMappingComponent)
export { AddKeyTypeMappingComponent as UnconnectedAddKeyTypeMappingComponent }
